import { useMemo, useState } from 'react';
import { BookOpen, Check, ChevronsUpDown, Flame, Globe2, ShieldCheck } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { CineCombo, slug } from './CineCombo';
import { cn } from '../../lib/utils';

const TYPE_HEADINGS = { series: 'Ultra-popular series', film: 'Blockbuster films', classic: 'Public-domain classics' };

export function AdaptationPicker({ catalog, value, onChange }) {
  const [open, setOpen] = useState(false);
  const titles = catalog.adaptation_titles;
  const selected = titles.find((t) => t.id === value.source_id);
  const culture = catalog.cultures.find((c) => c.id === value.culture_id);
  const set = (patch) => onChange({ ...value, ...patch });
  const trending = useMemo(() => [...titles].filter((t) => t.adaptation_count > 0)
    .sort((a, b) => b.adaptation_count - a.adaptation_count).slice(0, 6), [titles]);
  const cultureGroups = useMemo(() => Object.entries(catalog.cultures.reduce((acc, c) => (
    { ...acc, [c.region]: [...(acc[c.region] || []), c.label] }), {})).map(([heading, items]) => ({ heading, items })), [catalog.cultures]);

  return (
    <div className="space-y-4" data-testid="adaptation-picker">
      {trending.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="flex items-center gap-1 text-amber-300"><Flame size={13} /> Most adapted on Konnect:</span>
          {trending.map((t) => (
            <button key={t.id} type="button" onClick={() => set({ source_id: t.id, source_title: '' })}
              className="rounded-full border border-neutral-800 bg-neutral-900 px-2.5 py-1 text-neutral-300 hover:border-amber-400/50"
              data-testid={`adapt-trending-${t.id}`}>
              {t.title} <span className="text-amber-300">×{t.adaptation_count}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-[1fr_1fr]">
        <div className="space-y-1">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Story to adapt</div>
          <Popover open={open} onOpenChange={setOpen} modal>
            <PopoverTrigger asChild>
              <button type="button" data-testid="adapt-source-select"
                className="flex w-full items-center justify-between rounded-md border border-neutral-800 bg-neutral-950 px-3 py-2.5 text-left text-sm hover:border-amber-400/50">
                <span className={cn('truncate', !selected && 'text-neutral-500')}>{selected ? selected.title : `Choose from ${titles.length} famous stories…`}</span>
                <ChevronsUpDown size={14} className="opacity-60" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="studio-dark w-[420px] border-neutral-700 bg-neutral-950 p-0 text-neutral-100">
              <Command className="bg-neutral-950 text-neutral-100">
                <CommandInput placeholder="Search title, country or genre…" data-testid="adapt-source-search" />
                <CommandList className="max-h-80">
                  <CommandEmpty>Not listed — type it in the box below.</CommandEmpty>
                  {Object.entries(TYPE_HEADINGS).map(([type, heading]) => (
                    <CommandGroup key={type} heading={heading}>
                      {titles.filter((t) => t.type === type).map((t) => (
                        <CommandItem key={t.id} value={`${t.title} ${t.origin} ${t.genre}`} data-testid={`adapt-source-opt-${slug(t.id)}`}
                          onSelect={() => { set({ source_id: t.id, source_title: '' }); setOpen(false); }}>
                          <Check size={12} className={cn('mr-2 text-amber-300', value.source_id === t.id ? 'opacity-100' : 'opacity-0')} />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm">{t.title}</div>
                            <div className="text-[10px] text-neutral-500">{t.origin} · {t.year} · {t.genre}</div>
                          </div>
                          <span className={cn('ml-2 shrink-0 rounded-full px-1.5 text-[10px]', t.adaptation_count ? 'bg-amber-500/15 text-amber-200' : 'text-neutral-600')}>
                            ×{t.adaptation_count}
                          </span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <Input value={value.source_title} placeholder="…or type any other title (series or movie)" data-testid="adapt-source-custom"
            onChange={(e) => set({ source_title: e.target.value, source_id: '' })}
            className="border-neutral-800 bg-neutral-950 text-xs" />
        </div>
        <div className="space-y-2">
          <CineCombo label="Adapt for (language & culture)" value={culture?.label || ''} groups={cultureGroups}
            placeholder="Choose target culture…" testId="adapt-culture-select"
            onChange={(label) => set({ culture_id: catalog.cultures.find((c) => c.label === label)?.id || '' })} />
          {culture && <p className="text-[11px] leading-relaxed text-neutral-500"><Globe2 size={11} className="mr-1 inline" />{culture.notes}</p>}
        </div>
      </div>

      {selected && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3" data-testid="adapt-source-card">
          <div className="flex flex-wrap items-center gap-2">
            <BookOpen size={14} className="text-amber-300" />
            <span className="text-sm font-semibold">{selected.title}</span>
            <span className="text-[11px] text-neutral-500">{selected.origin} · {selected.year} · {selected.genre}</span>
            <Badge className="bg-amber-500/15 text-amber-200" data-testid="adapt-source-count">Adapted {selected.adaptation_count}× on Konnect</Badge>
            {selected.public_domain && <Badge className="bg-emerald-500/15 text-emerald-200">Public domain</Badge>}
          </div>
          <p className="mt-1.5 text-xs text-neutral-400">Core premise kept: {selected.premise}</p>
        </div>
      )}

      <div className="grid gap-2 md:grid-cols-3">
        {catalog.twist_levels.map((t) => (
          <button key={t.id} type="button" onClick={() => set({ twist_level: t.id })} data-testid={`adapt-twist-${t.id}`}
            className={cn('rounded-lg border p-3 text-left transition-colors', value.twist_level === t.id
              ? 'border-amber-400 bg-amber-500/10' : 'border-neutral-800 bg-neutral-950 hover:border-neutral-600')}>
            <div className="text-xs font-semibold text-neutral-100">{t.label}</div>
            <div className="mt-1 text-[11px] text-neutral-500">{t.instruction}</div>
          </button>
        ))}
      </div>

      <label className="flex items-start gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3 text-xs text-neutral-300">
        <Checkbox checked={value.originality_confirmed} onCheckedChange={(v) => set({ originality_confirmed: !!v })}
          className="mt-0.5" data-testid="adapt-originality-checkbox" />
        <span>
          <ShieldCheck size={12} className="mr-1 inline text-emerald-300" />
          <strong>Originality pledge.</strong> I'm making an original work inspired only by this story's core premise. Konnect will
          create a new title and replace every name, setting, costume and line of dialogue, then localise it and add new twists.
          No original scripts, names, logos or footage are used. A formal remake for commercial release may still need a format
          licence from the rights holder.
        </span>
      </label>
    </div>
  );
}
